import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Button, Card, CardBody } from 'reactstrap';

import { getOrderTrendSites } from './orderTrendApi';
import { getCategorySalesComparison } from './categorySalesApi';

const formatCount = (value) => new Intl.NumberFormat('en-US', {
  maximumFractionDigits: Number.isInteger(Number(value)) ? 0 : 2,
}).format(Number(value || 0));

const formatSignedCount = (value) => {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${formatCount(number)}`;
};

const formatPercent = (category) => {
  if (category.percent_difference === null) {
    return category.current_period_count > 0 ? 'New' : '—';
  }

  const value = Number(category.percent_difference || 0);
  return `${value > 0 ? '+' : ''}${formatCount(value)}%`;
};

const getDifferenceTone = (difference) => {
  if (Number(difference) > 0) {
    return 'is-up';
  }

  if (Number(difference) < 0) {
    return 'is-down';
  }

  return 'is-flat';
};

const padDatePart = (value) => String(value).padStart(2, '0');

const formatDateTimeInput = (date) => (
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`
  + `T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`
);

const getDefaultRanges = () => {
  const now = new Date();
  const currentStart = new Date(now);
  const daysSinceSaturday = (now.getDay() + 1) % 7;

  currentStart.setDate(currentStart.getDate() - daysSinceSaturday);
  currentStart.setHours(0, 0, 0, 0);

  const comparisonStart = new Date(currentStart);
  comparisonStart.setDate(comparisonStart.getDate() - (8 * 7));

  const comparisonEnd = new Date(now);
  comparisonEnd.setDate(comparisonEnd.getDate() - 7);

  return {
    comparisonEnd: formatDateTimeInput(comparisonEnd),
    comparisonStart: formatDateTimeInput(comparisonStart),
    currentEnd: formatDateTimeInput(now),
    currentStart: formatDateTimeInput(currentStart),
  };
};

const formatRangeDateTime = (value) => new Date(value).toLocaleString('en-US', {
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  month: 'short',
});

function CategorySalesWidget({ t }) {
  const [site, setSite] = useState('');
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [ranges, setRanges] = useState(getDefaultRanges);
  const [data, setData] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sitesRequested = useRef(false);

  const loadSites = () => {
    if (sitesRequested.current) {
      return;
    }

    sitesRequested.current = true;
    setSitesLoading(true);
    getOrderTrendSites()
      .then((response) => setSites(response.sites || []))
      .catch(() => {
        sitesRequested.current = false;
        setSites([]);
      })
      .finally(() => setSitesLoading(false));
  };

  useEffect(() => {
    if (request === null) {
      return undefined;
    }

    let mounted = true;

    setLoading(true);
    setError('');
    setData(null);
    getCategorySalesComparison(request)
      .then((response) => {
        if (mounted) {
          setData(response);
        }
      })
      .catch((requestError) => {
        if (mounted) {
          setError(
            requestError.response?.data?.error || t('Unable to load category sales'),
          );
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [request, t]);

  const calculate = () => {
    const currentStart = new Date(ranges.currentStart);
    const currentEnd = new Date(ranges.currentEnd);
    const comparisonStart = new Date(ranges.comparisonStart);
    const comparisonEnd = new Date(ranges.comparisonEnd);

    if ([currentStart, currentEnd, comparisonStart, comparisonEnd].some((value) => (
      Number.isNaN(value.getTime())
    ))) {
      setError(t('Choose valid current and comparison ranges'));
      return;
    }

    if (currentEnd <= currentStart || comparisonEnd <= comparisonStart) {
      setError(t('Each range end must be after its start'));
      return;
    }

    if (comparisonEnd >= currentStart) {
      setError(t('Comparison history must end before the current period'));
      return;
    }

    if ((currentEnd.getTime() - currentStart.getTime()) >= (7 * 24 * 60 * 60 * 1000)) {
      setError(t('Current period must be shorter than seven days'));
      return;
    }

    setError('');
    setRequest({
      comparisonEnd: comparisonEnd.toISOString(),
      comparisonStart: comparisonStart.toISOString(),
      currentEnd: currentEnd.toISOString(),
      currentStart: currentStart.toISOString(),
      site,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const changeSite = (event) => {
    setSite(event.target.value);
    setRequest(null);
    setData(null);
    setError('');
  };

  const changeRange = (event) => {
    const { name, value } = event.target;
    setRanges((currentRanges) => ({
      ...currentRanges,
      [name]: value,
    }));
    setRequest(null);
    setData(null);
    setError('');
  };

  const resetRanges = () => {
    setRanges(getDefaultRanges());
    setRequest(null);
    setData(null);
    setError('');
  };

  const categories = data?.categories || [];
  const rangeLabel = useMemo(() => {
    if (!data?.range?.current_start || !data?.range?.current_end) {
      return t('Saturday to now · 8-week average');
    }

    return `${formatRangeDateTime(data.range.current_start)} – ${formatRangeDateTime(data.range.current_end)}`;
  }, [data, t]);
  const significanceThreshold = data?.significance_threshold;
  const averageWeeks = data?.average_weeks || 8;

  return (
    <Card className="dashboard-panel dashboard-category-sales">
      <CardBody className="dashboard-panel__body">
        <div className="dashboard-panel__header dashboard-category-sales__header">
          <div>
            <span className="dashboard-panel__eyebrow">{t('BigCommerce')}</span>
            <h5 className="dashboard-panel__title">{t('Category Sales Changes')}</h5>
          </div>
          <span className="dashboard-panel__hint">
            {rangeLabel}
          </span>
        </div>

        <div className="dashboard-category-sales__ranges">
          <fieldset>
            <legend>{t('Current period')}</legend>
            <label htmlFor="category-sales-current-start">{t('From')}</label>
            <input
              id="category-sales-current-start"
              className="form-control form-control-sm"
              type="datetime-local"
              name="currentStart"
              value={ranges.currentStart}
              disabled={loading}
              onChange={changeRange}
            />
            <label htmlFor="category-sales-current-end">{t('To')}</label>
            <input
              id="category-sales-current-end"
              className="form-control form-control-sm"
              type="datetime-local"
              name="currentEnd"
              value={ranges.currentEnd}
              disabled={loading}
              onChange={changeRange}
            />
          </fieldset>
          <fieldset>
            <legend>{t('Comparison history')}</legend>
            <label htmlFor="category-sales-comparison-start">{t('From')}</label>
            <input
              id="category-sales-comparison-start"
              className="form-control form-control-sm"
              type="datetime-local"
              name="comparisonStart"
              value={ranges.comparisonStart}
              disabled={loading}
              onChange={changeRange}
            />
            <label htmlFor="category-sales-comparison-end">{t('To')}</label>
            <input
              id="category-sales-comparison-end"
              className="form-control form-control-sm"
              type="datetime-local"
              name="comparisonEnd"
              value={ranges.comparisonEnd}
              disabled={loading}
              onChange={changeRange}
            />
          </fieldset>
        </div>

        <div className="dashboard-category-sales__filter">
          <label htmlFor="category-sales-site">{t('Sale site')}</label>
          <select
            id="category-sales-site"
            className="form-select form-select-sm"
            value={site}
            disabled={loading}
            onFocus={loadSites}
            onChange={changeSite}
          >
            <option value="">{t('Overall')}</option>
            {sitesLoading && <option disabled>{t('Loading sites...')}</option>}
            {sites.map((siteOption) => (
              <option key={siteOption} value={siteOption}>{siteOption}</option>
            ))}
          </select>
          <Button
            color="light"
            size="sm"
            disabled={loading}
            onClick={resetRanges}
          >
            {t('Reset to now')}
          </Button>
          <Button
            color="primary"
            size="sm"
            disabled={loading}
            onClick={calculate}
          >
            {loading ? t('Calculating...') : t('Calculate')}
          </Button>
        </div>

        {significanceThreshold && (
          <div className="dashboard-category-sales__threshold">
            <span className="mdi mdi-filter-variant" />
            {t('Minimum')}
            {': '}
            {formatCount(significanceThreshold.minimum_weekly_count)}
            {' '}
            {t('period sales')}
            {' · '}
            {formatCount(significanceThreshold.minimum_count_difference)}
            {' '}
            {t('count difference')}
          </div>
        )}

        {!loading && error === '' && categories.length > 0 && (
          <div className="dashboard-category-sales__legend" aria-hidden="true">
            <span>{t('% difference')}</span>
            <span>{t('Count difference')}</span>
            <span>{t('Category')}</span>
          </div>
        )}

        {!loading && error === '' && data === null && (
          <div className="dashboard-category-sales__state">
            <span className="mdi mdi-chart-box-outline fs-2" />
            <span>{t('Compare Saturday through now with the same period from the previous eight weeks')}</span>
            <Button color="primary" size="sm" onClick={calculate}>
              {t('Calculate category changes')}
            </Button>
          </div>
        )}

        {loading && (
          <div className="dashboard-category-sales__state">
            <span className="mdi mdi-loading mdi-spin fs-2" />
            <span>{t('Loading category sales')}</span>
          </div>
        )}

        {!loading && error !== '' && (
          <div className="dashboard-category-sales__state is-error">
            <span className="mdi mdi-alert-circle-outline fs-2" />
            <span>{error}</span>
          </div>
        )}

        {!loading && error === '' && data?.active_tree_id === null && (
          <div className="dashboard-category-sales__state">
            <span className="mdi mdi-file-tree-outline fs-2" />
            <span>{t('No active BigCommerce category tree is configured')}</span>
          </div>
        )}

        {!loading && error === '' && data?.active_tree_id !== null && categories.length === 0 && (
          <div className="dashboard-category-sales__state">
            <span className="mdi mdi-chart-timeline-variant-shimmer fs-2" />
            <span>{t('No category sales were found for this period')}</span>
          </div>
        )}

        {!loading && error === '' && categories.length > 0 && (
          <div className="dashboard-category-sales__list">
            {categories.map((category) => {
              const tone = getDifferenceTone(category.count_difference);
              const countContext = `${formatCount(category.current_period_count)} ${t('current')} · ${formatCount(category.comparison_average_count)} ${t(`${averageWeeks}-week average`)}`;

              return (
                <div className="dashboard-category-sales__row" key={category.category_id}>
                  <strong className={`dashboard-category-sales__metric ${tone}`}>
                    {formatPercent(category)}
                  </strong>
                  <strong className={`dashboard-category-sales__metric ${tone}`}>
                    {formatSignedCount(category.count_difference)}
                  </strong>
                  <div className="dashboard-category-sales__category">
                    <strong>{category.category_name}</strong>
                    <span>{category.category_path}</span>
                    <small>{countContext}</small>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

CategorySalesWidget.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(CategorySalesWidget);
